export const ProtectiveOrderTypes = {
  takeProfit: 'takeProfit',
  stopLoss: 'stopLoss',
} as const

export type ProtectiveOrderType = (typeof ProtectiveOrderTypes)[keyof typeof ProtectiveOrderTypes]
export interface DxTradeBasePosition {
  positionCode: string
  version: number
  symbol: string
  quantity: number
  quantityNotional: number
  marginRate: number
}

export const DxTradeSides = {
  BUY: 'BUY',
  SELL: 'SELL',
} as const

export type DxTradeSide = (typeof DxTradeSides)[keyof typeof DxTradeSides] | null
export type DxTradeExecutableSide = Exclude<DxTradeSide, null>

export interface DxTradePosition extends DxTradeBasePosition {
  account: string
  side: DxTradeSide
  openTime: string
  lastUpdateTime: string
  openPrice: number
}

export interface DxTradeAccountMetricsPosition {
  positionCode: string | null
  version: number
  symbol: string | null
  rate: number
  fpl: number
  convRate: number
  bidOpen: number
  askOpen: number
  margin: number
  marginRate: number
  quantity: number
  quantityNotional: number
}

export interface DxTradeAccountBalanceMetric {
  version: number
  symbol: string | null
  balance: number
  allocatedFunds: number
  availableFunds: number
  availableBalanceForWithdrawal: number
  convRate: number
  precision: number
}

export interface DxTradeAccountMetrics {
  account: string | null
  version: number
  equity: number | null
  balance: number | null
  availableBalance: number | null
  availableFunds: number | null
  allocatedFunds: number | null
  credit: number | null
  marginFree: number | null
  openPL: number | null
  totalPL: number | null
  margin: number | null
  openPositionsCount: number
  openOrdersCount: number
  initialLoginToTV: string | null
  lastLoginToTV: string | null
  positions: DxTradeAccountMetricsPosition[] | null
  balances: DxTradeAccountBalanceMetric[] | null
}

export interface DxTradeAccountMetricsResponse {
  metrics: DxTradeAccountMetrics[] | null
}

export interface DxTradeOrderLeg {
  instrument: string | null
  positionEffect: string | null
  positionCode: string | null
  side: string | null
  price: number
  legRatio: number
  quantity: number
  filledQuantity: number
  remainingQuantity: number
  averagePrice: number
}

export interface DxTradeOrderLink {
  linkType: string | null
  linkedOrder: string | null
  linkedClientOrderId: string | null
}

export interface DxTradeOrderExecution {
  account: string | null
  executionCode: string | null
  orderCode: string | null
  updateOrderId: number
  version: number
  clientOrderId: string | null
  actionCode: string | null
  instrument: string | null
  status: string | null
  finalStatus: boolean
  filledQuantity: number
  lastQuantity: number
  filledQuantityNotional: number
  lastQuantityNotional: number
  remainingQuantity: number
  lastPrice: number
  averagePrice: number
  transactionTime: string
  marginRate: number
  rejectReason: string | null
  rejectCode: string | null
}

export interface DxTradeOrderCashTransaction {
  account: string | null
  transactionCode: string | null
  orderCode: string | null
  tradeCode: string | null
  version: number
  clientOrderId: string | null
  type: DxTradeCashTransactionType | null
  value: number
  currency: string | null
  transactionTime: string
}

export interface DxTradeCashTransactionResponse {
  account: string | null
  transactionCode: string | null
  orderCode: string | null
  tradeCode: string | null
  version: number
  clientOrderId: string | null
  type: string | null
  value: number
  currency: string | null
  transactionTime: string | null
}

export interface DxTradeCashTransfer {
  account: string | null
  version: number
  transferCode: string | null
  comment: string | null
  transactionTime: string | null
  cashTransactions: DxTradeCashTransactionResponse[] | null
}

export interface DxTradeAccountTransfersResponse {
  cashTransfers: DxTradeCashTransfer[] | null
}

export interface DxTradeSingleOrderRequest {
  account?: string | null
  orderCode?: string | null
  instrument?: string | null
  quantity?: number
  positionEffect?: string | null
  positionCode?: string | null
  side?: string | null
  tif?: string | null
  type?: string | null
  limitPrice?: number | null
  stopPrice?: number | null
  priceOffset?: number | null
  priceLink?: string | null
  expireDate?: string | null
}

export const DxTradeContingencyTypes = {
  IF_THEN: 'IF-THEN',
  OCO: 'OCO',
} as const

export type DxTradeContingencyType =
  (typeof DxTradeContingencyTypes)[keyof typeof DxTradeContingencyTypes]

export interface DxTradeOrderGroupRequest {
  orders: DxTradeSingleOrderRequest[]
  contingencyType: DxTradeContingencyType
}

export type FixOrderTypeCode = '1' | '2' | '3'

export const FIX_ORDER_TYPE: Record<string, FixOrderTypeCode> = {
  MARKET: '1',
  LIMIT: '2',
  STOP: '3',
} as const

export type FixTimeInForceCode = '1' | '6'

export type FixPositionEffectCode = 'O' | 'C'

export interface FixSingleOrderPayload {
  instrument: string
  quantity: number
  side: DxTradeExecutableSide
  ordType: FixOrderTypeCode
  positionEffect: FixPositionEffectCode
  timeInForce?: FixTimeInForceCode
  price?: number
  expireDate?: string
}

export interface FixOrderGroupPayload {
  orders: FixSingleOrderPayload[]
  contingencyType: DxTradeContingencyType
}

export interface DxTradeOrder {
  account: string | null
  version: number
  orderId: number
  orderCode: string | null
  clientOrderId: string | null
  actionCode: string | null
  legCount: number
  type: string | null
  instrument: string | null
  status: string | null
  finalStatus: boolean
  legs: DxTradeOrderLeg[] | null
  side: string | null
  tif: string | null
  expireDate: string | null
  openTime: string
  openPrice: number
  issueTime: string
  transactionTime: string
  marginRate: number
  links: DxTradeOrderLink[] | null
  executions: DxTradeOrderExecution[] | null
  cashTransactions: DxTradeOrderCashTransaction[] | null
  priceOffset: number
  priceLink: string | null
}

export interface DxTradeOrdersResponse {
  orders: DxTradeOrder[] | null
  nextPageTransactionTime: string | null
}

export interface DxTradeOrderCreateResponse {
  orderId: number
  updateOrderId: string
}

export interface DxTradeConversionRatesResponse {
  fromCurrency: string
  toCurrency: string
  convRate: number
}

export interface DxTradeConversionRatesApiResponse {
  conversionRates: DxTradeConversionRatesResponse[]
}

export interface DxTradeTradingHour {
  weekDay: string | null
  eventType: string | null
}

export interface DxTradeInstrumentMetadata {
  sourceTimestamp: string | null
}

interface DxTradeInstrumentBase {
  type: string | null
  symbol: string | null
  version: number | null
  description: string | null
  priceIncrement: number | null
  pipSize: number | null
  currency: string | null
  lotSize: number | null
  quantityIncrement: number | null
  multiplier: number | null
  underlying: string | null
  product: string | null
  firstCurrency: string | null
  tradingHours: DxTradeTradingHour[] | null
  currencyType: string | null
}

export interface DxTradeInstrumentDetails extends DxTradeInstrumentBase {
  metadata?: DxTradeInstrumentMetadata | null
}

export type InstrumentActionType = 'buy' | 'sell' | 'info' | 'news' | 'marketWidget'

export type InstrumentActionHandler = (
  instrument: Pick<DxTradeInstrumentDetails, 'symbol'>,
  action: InstrumentActionType
) => void

export interface DxTradeInstrumentResponse {
  instruments: DxTradeInstrumentDetails[] | null
}

export interface DxtradeInstrumentQuery {
  limit?: number
  startFrom?: number
  symbols?: string
  symbolDescription?: string
  assetClass?: string
  types?: string[]
}

export interface DxTradeInstrumentLimitValue {
  limitType: string | null
  value: number | null
}

export interface DxTradeInstrumentMarginTier {
  endVolume: string | null
  value: number | null
}

export interface DxTradeInstrumentMarginRate {
  rateType: string | null
  tiers: DxTradeInstrumentMarginTier[] | null
}

export interface DxTradeInstrumentDetailsCore {
  account: string | null
  type: string | null
  symbol: string | null
  symbolDescription: string | null
  tradingStatus: string | null
  minOrderSize: number | null
  maxOrderSize: DxTradeInstrumentLimitValue | null
  minOrderSizeIncrement: number | null
  marginRate: DxTradeInstrumentMarginRate | null
  assetClass: string | null
}

export interface DxTradeInstrumentDetailsWithMetadataCore extends DxTradeInstrumentDetailsCore {
  metadata: DxTradeInstrumentMetadata | null
}

export type DxTradeInstrumentDetailsResponseItem =
  | DxTradeInstrumentDetailsCore
  | DxTradeInstrumentDetailsWithMetadataCore

export type DxTradeInstrumentWithTradingMeta = DxTradeInstrumentDetailsResponseItem & {
  description?: string | null
  tradingHours?: DxTradeTradingHour[] | null
  priceIncrement?: number | null
  currency?: string | null
}

export interface DxTradeInstrumentDetailsResponse {
  instrumentDetails: DxTradeInstrumentDetailsResponseItem[] | null
}

export const DxTradeCashTransactionTypes = {
  COMMISSION: 'COMMISSION',
  FINANCING: 'FINANCING',
  DEPOSIT: 'DEPOSIT',
  WITHDRAWAL: 'WITHDRAWAL',
  SETTLEMENT: 'SETTLEMENT',
  COST: 'COST',
  EX_DIVIDEND: 'EX_DIVIDEND',
  REBATE: 'REBATE',
  NEGATIVE_BALANCE_CORRECTION: 'NEGATIVE_BALANCE_CORRECTION',
  BUST: 'BUST',
} as const

export type DxTradeCashTransactionType =
  (typeof DxTradeCashTransactionTypes)[keyof typeof DxTradeCashTransactionTypes]

export interface DxTradeOpenPositionsResponse {
  positions: DxTradePosition[] | null
}

export interface DxTradeAccountPosition {
  account: string | null
  version: number
  positionCode: string | null
  symbol: string | null
  quantity: number
  side: DxTradeSide | null
  quantityNotional: number
  openTime: string
  lastUpdateTime: string
  openPrice: number
  marginRate: number
  takeProfitPrice?: number | null
  stopLossPrice?: number | null
}

export interface DxTradeAccountPositionsResponse {
  positions: DxTradeAccountPosition[] | null
}

export const DxTradeOrderTypes = {
  MARKET: 'MARKET',
  LIMIT: 'LIMIT',
  STOP: 'STOP',
} as const

export type DxTradeOrderType = (typeof DxTradeOrderTypes)[keyof typeof DxTradeOrderTypes]

export const DxTradeOrderStatuses = {
  ACCEPTED: 'ACCEPTED',
  WORKING: 'WORKING',
  CANCELED: 'CANCELED',
  COMPLETED: 'COMPLETED',
  EXPIRED: 'EXPIRED',
  REJECTED: 'REJECTED',
} as const

export type DxTradeOrderStatus = (typeof DxTradeOrderStatuses)[keyof typeof DxTradeOrderStatuses]

export type TPSLMode = 'Price' | 'Amount' | 'Distance'

export const MarketDataEventTypes = {
  QUOTE: 'Quote',
  CANDLE: 'Candle',
} as const

export type MarketDataEventType = (typeof MarketDataEventTypes)[keyof typeof MarketDataEventTypes]

export const MarketDataEventFormats = {
  COMPACT: 'COMPACT',
} as const

export type MarketDataEventFormat =
  (typeof MarketDataEventFormats)[keyof typeof MarketDataEventFormats]

export interface MarketDataEventTypeRequest {
  type: MarketDataEventType
  format: MarketDataEventFormat
}

export interface MarketDataRequest {
  account: string
  symbols: string[]
  eventTypes: MarketDataEventTypeRequest[]
}

export interface MarketDataEventPayload {
  symbol: string
  type: MarketDataEventType
  format: MarketDataEventFormat
  time?: string
  bid: number
  ask: number
}

export interface MarketDataList {
  events: MarketDataEventPayload[]
}
export interface DxTradeRawCandle {
  timestamp: string
  symbol: string
  candleType: string
  openingPrice: number
  closingPrice: number
  highPrice: number
  lowPrice: number
}

export interface DxTradeCandlesMarketData {
  $type: 'CandlesMarketData'
  Candles: DxTradeRawCandle[]
}

export interface DxTradeSwapModeName {
  id: number
  name: string | null
}

export interface DxTradeSwapModeLocalizedName {
  id: number
  name: string | null
  customKey: string | null
}

export type DxTradeSwapMode = DxTradeSwapModeName | DxTradeSwapModeLocalizedName

export interface DxTradeTradingInfoOriginalSwapDto {
  mode: DxTradeSwapMode | null
  swap3Day: string | null
  long: number
  short: number
  digits: number
}

export interface DxTradeTradingInfoConvertedSwapDto {
  long: number
  short: number
}

export interface DxTradeTradingInfoDto {
  accountCurrency: string | null
  symbol: string | null
  date: string
  marginRequirement: number
  originalSwap: DxTradeTradingInfoOriginalSwapDto | null
  convertedSwap: DxTradeTradingInfoConvertedSwapDto | null
  conversionRate: number
  lots: number
  leverage: number
  tickSize: number
  pipValue: number
  profit: number
}

export type WatchlistType = 'PUBLIC' | 'PRIVATE'

export type WatchlistKind = 'REGULAR' | 'RECOMMENDED'

export interface WatchlistOwner {
  login?: string | null
  domain?: string | null
}

export interface UpdateWatchlistModel {
  name?: string
  description?: string
  type?: WatchlistType
  symbols?: string[]
  orderValue?: number | null
}

export interface WatchlistOrderValueItem {
  id: number
  orderValue: number
}

export interface BulkUpdateWatchlistOrderValues {
  orderValues: WatchlistOrderValueItem[] | null
}

export interface CreateWatchlistModel extends UpdateWatchlistModel {
  kind?: WatchlistKind | null
  owner?: WatchlistOwner | null
}

export interface DxtradeWatchlistsQuery {
  login?: string
  domain?: string
}

export interface WatchlistModel extends CreateWatchlistModel {
  id: number
  version: number
}

export interface DxTradePortfolioBalance {
  account: string
  version: number
  value: number
  currency: string
}

export interface DxTradePortfolioOwner {
  login: string
  domain?: string | null
}

export interface DxTradePortfolio {
  account: string
  version: number
  balances: DxTradePortfolioBalance[]
  positions: DxTradeAccountPosition[]
  orders: DxTradeOrder[]
  owner?: DxTradePortfolioOwner | null
}

export interface DxTradeAccountPortfolioWsMessage {
  $type: 'AccountPortfolio'
  Portfolios: DxTradePortfolio[]
}

export interface MarginRequirementTierDto {
  id: string
  marginRequirement: number
  maximumLeverage: number
  lotSizeMinimum: number
  lotSizeMaximum: number | null
}

export interface AssetClassMarginRequirementDto {
  id: string
  instrument: string | null
  assetClass: string | null
  tiers: MarginRequirementTierDto[] | null
}
